/**
 * Debt Metrics (formerly Facility & Covenants) — current-quarter progress against the active
 * financial model. Four clickable metric cards (Generation / Revenue /
 * CFADS / DSCR) at the top show actual-to-date, the day-pro-rated
 * target, and the end-of-quarter projection if the current pace
 * continues. Clicking a card loads a big chart below showing the
 * in-quarter progression for that metric.
 *
 * Forecast at sub-quarter granularity is a flat daily rate
 * (quarter_value / days_in_quarter), per the spec. Actuals come from
 * atlas SCADA + uploaded PPAs (no allocation-engine middle layer).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Card, CardContent, Alert,
  CircularProgress, Button, Chip,
  ToggleButton, ToggleButtonGroup, Select, MenuItem,
  Collapse, Table, TableHead, TableBody, TableRow, TableCell,
  Skeleton,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { BarPlot } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsLegend } from '@mui/x-charts/ChartsLegend';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsAxisHighlight } from '@mui/x-charts/ChartsAxisHighlight';
import { useXAxis, useYAxis } from '@mui/x-charts/hooks';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const GREEN = '#1f9d57';
const RED   = '#e5384f';
const GREY  = '#334155';   // forecast — slate-ink (reads as target, not disabled)
const PROJ  = 'rgba(217,138,43,0.85)';   // projection — stronger amber so it doesn't wash out next to actual
const AMBER = '#d98a2b';
// Revenue-source palette for the default "All" split (PPA / merchant / LGC
// stacked in one bar). PPA reuses the on-track green so "contracted" reads as
// secure; merchant = blue (spot), LGC = gold (green certificates).
const SRC_COLOR = { ppa: GREEN, merchant: '#3b82f6', lgc: '#E9B065' };

// ── formatters ─────────────────────────────────────────────────────────────

const fmtAud = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e9) return `A$${(v / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `A$${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `A$${(v / 1e3).toFixed(0)}k`;
  return `A$${v.toFixed(0)}`;
};
const fmtMwh = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString('en-AU')}`);
const fmtX   = (v) => (v == null ? '—' : `${Number(v).toFixed(2)}x`);
const fmtPct = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(1)}%`);

const valueFormatter = (unit) => (v) => {
  if (v == null) return '—';
  if (unit === 'AUD') return fmtAud(v);
  if (unit === 'MWh') return `${fmtMwh(v)} MWh`;
  if (unit === 'x')   return fmtX(v);
  return Number(v).toLocaleString('en-AU');
};

const fmtBucketDate = (iso, bucket) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return iso;
  // Bucket-aware labels: a "01 Apr" tick on a quarterly axis was
  // confusing — users read it as "April only". Show "Q1 2026" /
  // "Q2 2026" instead; full year on yearly.
  if (bucket === 'quarterly') {
    return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
  }
  if (bucket === 'yearly') {
    return `${d.getFullYear()}`;
  }
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
};

// ── 4 metric cards (clickable) ─────────────────────────────────────────────

const METRICS = [
  { key: 'generation', label: 'Generation', unit: 'MWh', fmt: fmtMwh },
  { key: 'revenue',    label: 'Revenue',    unit: 'AUD', fmt: fmtAud },
  { key: 'cfads',      label: 'CFADS',      unit: 'AUD', fmt: fmtAud },
  { key: 'dscr',       label: 'DSCR',       unit: 'x',   fmt: fmtX   },
];

// ── Compact sparkline that lives inside each metric card ──────────────────
//
// Shows three lines through the quarter so the user can see the gap and
// the projection at a glance:
//   • grey line  — cumulative forecast (straight from origin to forecast_period)
//   • solid line — cumulative actual, only up to "today"
//   • dashed     — projected continuation from today's actual to projected_end
//
// DSCR is a ratio, not a cumulative quantity — we plot the implied actual
// DSCR per bucket as a line against the flat forecast (and the lock-up
// threshold when the model provides it).
function CardSparkline({ metric, data, thresholds }) {
  // Horizontal progress meter: actual fill (green if on/above the
  // reference, red if below) + projection in amber stacked on the same
  // track, with a vertical grey line marking the reference target. For
  // DSCR the reference is the lock-up covenant (the meaningful
  // benchmark); for other metrics it's the model's period forecast.
  // The track scales to max(reference, actual+projection) so an
  // over-run still fits.
  const isDscr = metric.key === 'dscr';
  const reference = isDscr
    ? (thresholds?.lockup_dscr ?? data?.forecast_period)
    : data?.forecast_period;
  if (!data || reference == null) return null;

  const projTotal = data.projected_end;

  let actualVal, projExt, above;
  if (isDscr) {
    // DSCR is a ratio, not a cumulative — there's no "projection on top
    // of actual" to stack. We show the implied/projected DSCR as the
    // filled segment and compare it directly to the covenant.
    actualVal = projTotal ?? data.actual_to_date ?? 0;
    projExt   = 0;
    above     = actualVal >= reference;
  } else {
    const actual = data.actual_to_date ?? 0;
    projExt   = (projTotal != null) ? Math.max(0, projTotal - actual) : 0;
    actualVal = actual;
    above     = (actual + projExt) >= reference;
  }

  const scale       = Math.max(reference, actualVal + projExt) || 1;
  const actualPct   = (actualVal / scale) * 100;
  const projPct     = (projExt   / scale) * 100;
  const referencePct = (reference / scale) * 100;

  return (
    <Box sx={{
      // outer wrapper isn't clipped so the forecast tick can stick
      // out above/below the track without being cut off.
      position: 'relative', height: 14, width: '100%',
      mt: 0.75, mb: 0.75,
    }}>
      <Box sx={{
        position: 'absolute', inset: '2px 0',
        bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 1, overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${actualPct}%`, bgcolor: above ? GREEN : RED,
        }} />
        {projPct > 0 && (
          <Box sx={{
            position: 'absolute', left: `${actualPct}%`, top: 0, bottom: 0,
            width: `${projPct}%`, bgcolor: PROJ,
          }} />
        )}
      </Box>
      {/* Reference tick (covenant for DSCR, forecast otherwise) —
          full-height of the wrapper (taller than the track) and 6px
          wide so it pops as an unmissable target marker. */}
      <Box sx={{
        position: 'absolute', top: 0, bottom: 0,
        left: `calc(${referencePct}% - 3px)`, width: 6,
        bgcolor: isDscr ? RED : GREY, borderRadius: 1,
        boxShadow: '0 0 0 1px rgba(255,255,255,0.85)',
      }} />
    </Box>
  );
}

// Custom forecast layer for the BigChart: draws one short horizontal
// dotted segment per bucket at the forecast value, ~80% of the band
// width. Replaces the connected line so each bucket reads as its own
// discrete target rather than a single sweeping reference line.
function ForecastTicks({ data, color = GREY }) {
  const xAxis = useXAxis();
  const yAxis = useYAxis();
  if (!xAxis?.scale || !yAxis?.scale || !data) return null;
  const xScale = xAxis.scale;
  const yScale = yAxis.scale;
  const bandWidth = typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
  if (!bandWidth) return null;
  const half = bandWidth * 0.4;   // 80% width centred on the band
  return (
    <g pointerEvents="none">
      {data.map((v, i) => {
        if (v == null) return null;
        const label = xAxis.data?.[i];
        const x = xScale(label);
        const y = yScale(v);
        if (x == null || y == null) return null;
        const cx = x + bandWidth / 2;
        return (
          <line
            key={i}
            x1={cx - half} x2={cx + half} y1={y} y2={y}
            stroke={color} strokeWidth={3}
            strokeDasharray="2 3" strokeLinecap="round"
          />
        );
      })}
    </g>
  );
}


function MetricCard({ m, data, quarter, selected, onClick, series, thresholds }) {
  const d = data || {};
  // DSCR compares against the lock-up covenant (the meaningful
  // benchmark) — not against the model's own quarter-by-quarter
  // forecast. Falls back to forecast_period if the active model has no
  // covenant on its summary_json.
  const isDscr = m.key === 'dscr';
  const benchmark = isDscr
    ? (thresholds?.lockup_dscr ?? d.forecast_period)
    : d.forecast_period;
  const benchmarkLabel = isDscr
    ? (thresholds?.lockup_dscr != null ? 'lock-up covenant' : 'forecast')
    : 'forecast';
  const variance = (isDscr && benchmark != null && d.projected_end != null)
    ? (d.projected_end - benchmark)
    : d.variance_abs;
  const variancePct = (isDscr && benchmark != null && d.projected_end != null && benchmark !== 0)
    ? ((d.projected_end - benchmark) / Math.abs(benchmark) * 100)
    : d.variance_pct;
  const varColor = variance == null ? 'text.secondary'
                   : variance >= 0 ? GREEN : RED;
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        flex: '1 1 200px', minWidth: 200, cursor: 'pointer',
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
        transition: 'all 0.15s ease',
        '&:hover': { borderColor: 'primary.light',
                     boxShadow: selected ? undefined : '0 2px 8px rgba(0,0,0,0.06)' },
        bgcolor: selected ? 'rgba(217,138,43,0.04)' : 'background.paper',
      }}
    >
      <CardContent sx={{ pb: '14px !important', pt: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography sx={{
            fontSize: '0.66rem', color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            fontWeight: 700, flex: 1,
          }}>
            {m.label}
          </Typography>
          <Chip size="small" label={m.unit} sx={{ height: 18, fontSize: '0.62rem' }} />
        </Stack>

        {/* Actual to-date vs day-pro-rated target */}
        <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, fontFamily: 'monospace',
                          lineHeight: 1.1, mb: 0.25 }}>
          {d.actual_to_date == null ? '—' : m.fmt(d.actual_to_date)}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1 }}>
          {/* DSCR's target is the period's DSCR (debt service is paid per
              period, not accrued daily) — "rolling" only fits the
              cumulative metrics whose target grows with elapsed days. */}
          of {m.fmt(d.forecast_to_date)} {isDscr ? 'period' : 'rolling'} target · day {quarter.days_elapsed} of {quarter.days_total}
        </Typography>

        <Box sx={{ height: 4, bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 2, mb: 1 }}>
          <Box sx={{
            height: '100%', width: `${Math.min(100, quarter.pct_elapsed || 0)}%`,
            bgcolor: 'primary.main', borderRadius: 2, transition: 'width 0.3s ease',
          }} />
        </Box>

        {/* Sparkline: cumulative actual vs forecast with dashed projection
            so the gap-to-date and end-of-quarter trajectory are visible
            at a glance, not just in the headline numbers. */}
        <CardSparkline metric={m} data={d} thresholds={thresholds} />

        {/* Projected end-of-quarter + variance */}
        <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary',
                          textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Projected (at current pace)
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, fontFamily: 'monospace',
                            color: varColor }}>
            {d.projected_end == null ? 'no actuals yet' : m.fmt(d.projected_end)}
          </Typography>
          {variance != null && (
            <Stack direction="row" spacing={0.25} alignItems="center"
                   sx={{ fontSize: '0.74rem', fontWeight: 600, color: varColor }}>
              {variance >= 0
                ? <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                : <ArrowDownwardIcon sx={{ fontSize: 13 }} />}
              {fmtPct(variancePct)}
            </Stack>
          )}
        </Stack>
        <Typography sx={{ fontSize: '0.66rem', color: 'text.disabled', mt: 0.25 }}>
          vs {m.fmt(benchmark)} {benchmarkLabel}
          {isDscr && thresholds?.default_dscr != null
            ? ` · ${m.fmt(thresholds.default_dscr)} default`
            : ''}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ── Big in-quarter chart (selected metric) ─────────────────────────────────

function BigChart({ metric, series, thresholds, bucket, onBucketChange,
                    periodKind, bucketOptions }) {
  // Fill the rest of the viewport below the card's top — no fixed 320px
  // height that leaves whitespace below on tall screens.
  const cardRef = useRef(null);
  const [chartH, setChartH] = useState(440);
  // Revenue source filter (totals by default; user picks PPA/Merchant/LGC
  // when they want to drill into one source).
  const [revSource, setRevSource] = useState('all');
  useEffect(() => {
    const compute = () => {
      const el = cardRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // ~140px for the card padding + chart header + page bottom gutter.
      const h = Math.max(280, Math.floor(window.innerHeight - top - 140));
      setChartH(h);
    };
    compute();
    window.addEventListener('resize', compute);
    const t = window.setTimeout(compute, 80);    // re-measure after layout
    return () => { window.removeEventListener('resize', compute); clearTimeout(t); };
  }, [metric, bucket]);

  const m = METRICS.find((x) => x.key === metric);
  const buckets = series?.buckets || [];
  if (!buckets.length) {
    return (
      <Card ref={cardRef} variant="outlined" sx={{ p: 4, textAlign: 'center', borderColor: 'divider' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No in-period data yet.
        </Typography>
      </Card>
    );
  }
  const labels = buckets.map((b) => fmtBucketDate(b.date, bucket || series?.bucket));
  const fmt = valueFormatter(m.unit);

  if (m.key === 'dscr') {
    // Lines: implied actual + flat-rate forecast + optional stress overlays
    // (P90 / combined) + optional model thresholds. Stress lines only render
    // when the bank model contains the corresponding scenario rows.
    const actual    = buckets.map((b) => b.dscr_actual ?? null);
    const forecast  = buckets.map((b) => b.dscr_forecast ?? null);
    const p90       = buckets.map((b) => b.dscr_p90_forecast ?? null);
    const stressed  = buckets.map((b) => b.dscr_stressed_forecast ?? null);
    const hasP90      = p90.some((v) => v != null);
    const hasStressed = stressed.some((v) => v != null);
    const seriesArr = [
      { data: actual,   label: 'Implied actual', color: AMBER, showMark: true,
        valueFormatter: fmt, curve: 'linear' },
      { data: forecast, label: 'Base case', color: GREY, showMark: false,
        valueFormatter: fmt, curve: 'linear' },
    ];
    if (hasP90) {
      seriesArr.push({
        data: p90, label: 'P90 (downside)', color: '#a77b3a',  // muted amber
        showMark: false, valueFormatter: fmt, curve: 'linear',
      });
    }
    if (hasStressed) {
      seriesArr.push({
        data: stressed, label: 'Combined stress', color: '#7d2f3a',  // deep red
        showMark: false, valueFormatter: fmt, curve: 'linear',
      });
    }
    if (thresholds?.lockup_dscr != null) {
      seriesArr.push({
        data: labels.map(() => thresholds.lockup_dscr),
        label: `Lock-up ${Number(thresholds.lockup_dscr).toFixed(2)}x`,
        color: RED, showMark: false, curve: 'linear',
      });
    }
    return (
      <Card ref={cardRef} variant="outlined" sx={{ borderColor: 'divider' }}>
        <CardContent>
          <ChartHeader metric={m} series={series}
                       bucket={bucket} onBucketChange={onBucketChange}
                       periodKind={periodKind} bucketOptions={bucketOptions} />
          <LineChart
            height={chartH}
            xAxis={[{ scaleType: 'point', data: labels }]}
            series={seriesArr}
            slotProps={{ legend: { hidden: false, position: { vertical: 'top', horizontal: 'middle' } } }}
            margin={{ left: 60, right: 24, top: 36, bottom: 36 }}
          />
        </CardContent>
      </Card>
    );
  }

  // Bars for Generation / Revenue / CFADS. Actual split into above/below
  // per bucket we render forecast (own bar) next to an actual+projection
  // stacked bar. The projection segment (lighter amber) sits on top of
  // actual for the CURRENT bucket only — it represents "if the current
  // pace continues, this much more will land".
  // Past buckets: projection = 0 (actual is final). Future buckets: no
  // actual segment, just the grey forecast bar.
  //
  // For revenue with a single-source filter, we pull source-specific
  // forecast/actual fields and pro-rate the per-bucket projection by the
  // source's share of that bucket's total forecast (backend only computes
  // a single revenue_projection; same-pace assumption flows through).
  const isRevSource = m.key === 'revenue' && revSource !== 'all';
  const fcKeyMap = { ppa: 'revenue_ppa_forecast',
                     merchant: 'revenue_merchant_forecast',
                     lgc: 'revenue_lgc_forecast' };
  const acKeyMap = { ppa: 'revenue_contracted_actual',
                     merchant: 'revenue_merchant_actual',
                     lgc: 'revenue_lgc_actual' };
  const actualKey   = isRevSource ? acKeyMap[revSource]   : `${m.key}_actual`;
  const forecastKey = isRevSource ? fcKeyMap[revSource]   : `${m.key}_forecast`;
  const totalFcKey  = `${m.key}_forecast`;        // for projection share
  const projectionKey = `${m.key}_projection`;
  const sourceLabel = { ppa: 'PPA', merchant: 'Merchant', lgc: 'LGC' }[revSource];
  const projFor = (b) => {
    const total = b[projectionKey];
    if (!total || total <= 0) return 0;
    if (!isRevSource) return total;
    const share = b[totalFcKey] ? (b[forecastKey] || 0) / b[totalFcKey] : 0;
    return total * share;
  };
  // Green/red split on the actual segment depends on whether the bar's
  // PROJECTED total (actual + projection) meets the forecast.
  const above = buckets.map((b) => {
    if (b[actualKey] == null) return null;
    const total = b[actualKey] + projFor(b);
    return total >= b[forecastKey] ? b[actualKey] : null;
  });
  const below = buckets.map((b) => {
    if (b[actualKey] == null) return null;
    const total = b[actualKey] + projFor(b);
    return total <  b[forecastKey] ? b[actualKey] : null;
  });
  const proj = buckets.map((b) => { const v = projFor(b); return v > 0 ? v : null; });
  const fc   = buckets.map((b) => b[forecastKey] ?? null);
  // Cadence-prefixed forecast label so the tooltip number is unambiguous
  // (a "Forecast: 16,101 MWh" hover doesn't tell the user the period).
  const cadence = ({ daily: 'Daily', weekly: 'Weekly',
                     monthly: 'Monthly', quarterly: 'Quarterly' }
                   [bucket || series?.bucket || 'weekly']) || 'Period';
  const fcLabel = isRevSource
    ? `${cadence} forecast — ${sourceLabel}`
    : `${cadence} forecast`;
  const acOnLabel  = isRevSource ? `Actual — ${sourceLabel} (on-track)`  : 'Actual (on-track)';
  const acOffLabel = isRevSource ? `Actual — ${sourceLabel} (off-track)` : 'Actual (off-track)';

  // Default revenue view = show the PPA / merchant / LGC actuals stacked in
  // one bar (they sum exactly to revenue_actual — see actuals.py), so the
  // composition is visible without toggling. The on-track/off-track green/red
  // split is kept only when drilling into a single source, where "is this
  // source hitting its forecast" is the meaningful question.
  const revAll = m.key === 'revenue' && revSource === 'all';
  const srcActual = (key) => buckets.map((b) => b[key] ?? null);
  const chartSeries = revAll ? [
    { type: 'bar', data: srcActual('revenue_contracted_actual'), label: 'PPA (actual)',
      color: SRC_COLOR.ppa, stack: 'ap', valueFormatter: fmt },
    { type: 'bar', data: srcActual('revenue_merchant_actual'), label: 'Merchant (actual)',
      color: SRC_COLOR.merchant, stack: 'ap', valueFormatter: fmt },
    { type: 'bar', data: srcActual('revenue_lgc_actual'), label: 'LGC (actual)',
      color: SRC_COLOR.lgc, stack: 'ap', valueFormatter: fmt },
    { type: 'bar', data: proj, label: 'Projection (at current pace)', color: PROJ,
      stack: 'ap', valueFormatter: fmt },
    { type: 'line', data: fc, label: `${cadence} forecast`, color: GREY,
      curve: 'linear', showMark: false, valueFormatter: fmt },
  ] : [
    { type: 'bar', data: above, label: acOnLabel,  color: GREEN,
      stack: 'ap', valueFormatter: fmt },
    { type: 'bar', data: below, label: acOffLabel, color: RED,
      stack: 'ap', valueFormatter: fmt },
    { type: 'bar', data: proj,  label: 'Projection (at current pace)', color: PROJ,
      stack: 'ap', valueFormatter: fmt },
    // Forecast stays in the series array so the legend shows it, but it's
    // rendered as discrete per-bucket dotted segments by ForecastTicks below.
    { type: 'line', data: fc,   label: fcLabel, color: GREY,
      curve: 'linear', showMark: false, valueFormatter: fmt },
  ];
  return (
    <Card ref={cardRef} variant="outlined" sx={{ borderColor: 'divider' }}>
      <CardContent>
        <ChartHeader metric={m} series={series}
                     bucket={bucket} onBucketChange={onBucketChange}
                     periodKind={periodKind} bucketOptions={bucketOptions}
                     revSource={revSource} onRevSourceChange={setRevSource} />
        <ChartContainer
          height={chartH}
          xAxis={[{ scaleType: 'band', data: labels }]}
          series={chartSeries}
          margin={{ left: 70, right: 24, top: 36, bottom: 36 }}
        >
          <BarPlot />
          <ForecastTicks data={fc} />
          <ChartsXAxis />
          <ChartsYAxis />
          <ChartsAxisHighlight x="band" />
          <ChartsLegend />
          <ChartsTooltip trigger="axis" />
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

const BUCKET_LABEL = {
  daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly',
  quarterly: 'Quarterly', yearly: 'Yearly',
};

function ChartHeader({ metric, series, bucket, onBucketChange,
                      bucketOptions, periodKind,
                      revSource, onRevSourceChange }) {
  const opts = bucketOptions ||
    (periodKind === 'year'
      ? ['weekly', 'monthly', 'quarterly', 'yearly']
      : ['daily', 'weekly', 'monthly', 'quarterly']);
  // Section heading says "in-period" — a quarter OR a year can be selected,
  // so a kind-specific word was misleading (meeting 2026-06-10).
  const periodWord = 'in-period';
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
        {metric.label} · {periodWord} progress
      </Typography>
      <Chip size="small" label={metric.unit} sx={{ height: 18, fontSize: '0.66rem' }} />
      <Box sx={{ flex: 1 }} />
      {metric.key === 'revenue' && onRevSourceChange && (
        <ToggleButtonGroup
          size="small" exclusive value={revSource || 'all'}
          onChange={(_, v) => v && onRevSourceChange(v)}
          sx={{ '& .MuiToggleButton-root': { py: 0.25, fontSize: '0.68rem' } }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="ppa">PPA</ToggleButton>
          <ToggleButton value="merchant">Merchant</ToggleButton>
          <ToggleButton value="lgc">LGC</ToggleButton>
        </ToggleButtonGroup>
      )}
      {onBucketChange && (
        <ToggleButtonGroup
          size="small" exclusive value={bucket || series?.bucket || opts[0]}
          onChange={(_, v) => v && onBucketChange(v)}
          sx={{ '& .MuiToggleButton-root': { py: 0.25, fontSize: '0.68rem' } }}
        >
          {opts.map((v) => (
            <ToggleButton key={v} value={v}>{BUCKET_LABEL[v]}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    </Stack>
  );
}

// ── Side tiles (DSRA) ──────────────────────────────────────────────────────
//
// Rendered as a small horizontal strip below the headline cards. Each tile
// hides itself when its data isn't available, so the strip degrades to one
// (or zero) tiles cleanly — keeps the layout calm on portfolios that don't
// have a bank model loaded yet.

function DsraTile({ dsra }) {
  if (!dsra) return null;
  const target   = dsra.target_aud;
  const required = dsra.required_aud;
  const headroom = dsra.headroom_pct;
  const ok       = headroom != null && headroom >= 0;
  return (
    <Card variant="outlined" sx={{
      flex: '1 1 280px', minWidth: 280, borderColor: 'divider',
    }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: 'text.secondary',
                            textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            DSRA
          </Typography>
          <Typography sx={{ fontSize: '0.66rem', color: 'text.disabled' }}>
            {dsra.look_forward_periods}-period look-forward
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2.5} alignItems="baseline">
          <Box>
            <Typography sx={{ fontSize: '0.66rem', color: 'text.disabled' }}>Required</Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>{fmtAud(required)}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.66rem', color: 'text.disabled' }}>Target</Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>{fmtAud(target)}</Typography>
          </Box>
          {headroom != null && (
            <Box>
              <Typography sx={{ fontSize: '0.66rem', color: 'text.disabled' }}>Headroom</Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: ok ? GREEN : RED }}>
                {fmtPct(headroom)}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

// Debt Service summary — senior P&I (the DSCR denominator) as the
// headline, clearly labelled as excluding bank/DSRF/agency fees, with the
// all-in figure + fee delta shown beneath, plus the P&I split, coupon and
// outstanding balance. Mirrors the Metis feedback (2026-06-09): use the
// bank model's KPI-tab senior figure and make the basis explicit.
function DebtServiceTile({ ds, periodLabel, selected, onClick }) {
  if (!ds) return null;
  const fmtCoupon = (v) => (v == null ? '—' : `${Number(v).toFixed(2)}%`);
  // Only surface the all-in/fee line when it's meaningfully above senior
  // (a model without a separate all-in line reports them equal).
  const hasFees = ds.all_in_aud != null && ds.fees_aud != null && ds.fees_aud > 0.5;
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        // Same flex rhythm as MetricCard so it sits as the 5th card in
        // the row; the inner stat row wraps to fit the narrower width.
        flex: '1 1 200px', minWidth: 200, cursor: 'pointer',
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
        transition: 'all 0.15s ease',
        '&:hover': { borderColor: 'primary.light',
                     boxShadow: selected ? undefined : '0 2px 8px rgba(0,0,0,0.06)' },
        bgcolor: selected ? 'rgba(217,138,43,0.04)' : 'background.paper',
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: 'text.secondary',
                            textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Debt Service
          </Typography>
          <Typography sx={{ fontSize: '0.66rem', color: 'text.disabled' }}>
            {periodLabel}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, fontFamily: 'monospace',
                          lineHeight: 1.1 }}>
          {fmtAud(ds.senior_pi_aud)}
        </Typography>
        <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', mb: 1 }}>
          Senior P&amp;I · excl. bank, DSRF &amp; agency fees
        </Typography>
        <Stack direction="row" spacing={2.5} alignItems="baseline" useFlexGap flexWrap="wrap">
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>Principal</Typography>
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700 }}>{fmtAud(ds.principal_aud)}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>Interest</Typography>
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700 }}>{fmtAud(ds.interest_aud)}</Typography>
          </Box>
          {ds.sweep_aud > 0 && (
            <Box>
              <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>Cash sweep</Typography>
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 700 }}>{fmtAud(ds.sweep_aud)}</Typography>
            </Box>
          )}
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>All-in interest</Typography>
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700 }}>{fmtCoupon(ds.coupon_pct)}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>Outstanding</Typography>
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700 }}>{fmtAud(ds.balance_aud)}</Typography>
          </Box>
        </Stack>
        {hasFees && (
          <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', mt: 0.75 }}>
            Incl. all fees: <b style={{ color: '#0a1626' }}>{fmtAud(ds.all_in_aud)}</b>
            {' '}· bank/DSRF/agency fees {fmtAud(ds.fees_aud)}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// Month label for schedule charts ("Apr 26").
const fmtSchedMonth = (iso) => {
  const [y, m] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1))
    .toLocaleDateString('en-AU', { month: 'short', year: '2-digit', timeZone: 'UTC' });
};

// Placeholder for the Debt Service card while /financials is in flight —
// the main period-progress fetch gates the page render, but the debt
// service summary comes from the (slower) /financials side-fetch. The
// skeleton keeps the 5th card slot occupied so the row doesn't reflow
// when the data lands.
function DebtServiceSkeleton() {
  return (
    <Card variant="outlined" sx={{ flex: '1 1 200px', minWidth: 200, borderColor: 'divider' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: 'text.secondary',
                          textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
          Debt Service
        </Typography>
        <Skeleton variant="text" width="70%" height={34} />
        <Skeleton variant="text" width="55%" height={16} sx={{ mb: 1 }} />
        <Stack direction="row" spacing={2.5}>
          <Skeleton variant="rounded" width={64} height={34} />
          <Skeleton variant="rounded" width={64} height={34} />
          <Skeleton variant="rounded" width={64} height={34} />
        </Stack>
      </CardContent>
    </Card>
  );
}

// In-period debt service chart — loaded when the Debt Service tile is
// selected, mirroring how the metric cards load their big chart. Debt
// service is a contractual SCHEDULE, not a measured actual, so the chart
// shows the schedule itself: stacked principal + interest bars per month
// within the selected window. Closing balance lives in its own line
// chart below (BalanceChart) — its A$100M+ scale would crush the
// monthly P&I bars on a shared plot.
function DebtServiceChart({ rows, periodStart, periodEnd }) {
  // ISO string compare is safe for YYYY-MM-DD.
  const win = (rows || []).filter((r) =>
    r.period_start &&
    (!periodStart || r.period_start >= periodStart) &&
    (!periodEnd || r.period_start <= periodEnd));
  if (!win.length) {
    return (
      <Card variant="outlined" sx={{ p: 4, textAlign: 'center', borderColor: 'divider' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {(rows || []).length
            ? 'No debt schedule periods in the selected window.'
            : 'Debt schedule unavailable from the active model — re-activate the financial model in Documents to seed it.'}
        </Typography>
      </Card>
    );
  }
  const labels = win.map((r) => fmtSchedMonth(r.period_start));
  return (
    <Card variant="outlined" sx={{ borderColor: 'divider' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
            Debt service · senior P&amp;I schedule
          </Typography>
          <Chip size="small" label="AUD" sx={{ height: 18, fontSize: '0.66rem' }} />
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
            excl. bank, DSRF &amp; agency fees
          </Typography>
        </Stack>
        <ChartContainer
          height={360}
          xAxis={[{ scaleType: 'band', data: labels }]}
          yAxis={[{ id: 'pi', valueFormatter: (v) => fmtAud(v) }]}
          series={[
            { type: 'bar', data: win.map((r) => r.principal_aud), label: 'Principal',
              stack: 'pi', color: GREY, yAxisId: 'pi', valueFormatter: fmtAud },
            { type: 'bar', data: win.map((r) => r.interest_aud), label: 'Interest',
              stack: 'pi', color: '#9aa6b5', yAxisId: 'pi', valueFormatter: fmtAud },
            // Cash sweep — discretionary extra principal, stacked on top
            // so the bar total reads P&I + sweep paid in the period.
            { type: 'bar', data: win.map((r) => r.sweep_aud || 0), label: 'Cash sweep',
              stack: 'pi', color: PROJ, yAxisId: 'pi', valueFormatter: fmtAud },
          ]}
          margin={{ left: 70, right: 24, top: 36, bottom: 36 }}
        >
          <BarPlot />
          <ChartsXAxis />
          <ChartsYAxis axisId="pi" />
          <ChartsAxisHighlight x="band" />
          <ChartsLegend />
          <ChartsTooltip trigger="axis" />
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

// Closing balance — its own line chart, scoped to the selected period
// like the P&I bars above it.
function BalanceChart({ rows, periodStart, periodEnd }) {
  const pts = (rows || []).filter((r) =>
    r.period_start &&
    (!periodStart || r.period_start >= periodStart) &&
    (!periodEnd || r.period_start <= periodEnd));
  if (!pts.length) return null;
  const labels = pts.map((r) => fmtSchedMonth(r.period_start));
  return (
    <Card variant="outlined" sx={{ borderColor: 'divider', mt: 1.5 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
            Closing balance
          </Typography>
          <Chip size="small" label="AUD" sx={{ height: 18, fontSize: '0.66rem' }} />
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
            senior debt outstanding
          </Typography>
        </Stack>
        <LineChart
          height={260}
          xAxis={[{ scaleType: 'point', data: labels }]}
          // Anchor at A$0 — auto-scaling zoomed the axis to the window's
          // narrow value range, so a ~0.4%/quarter paydown looked like the
          // debt was falling off a cliff.
          yAxis={[{ min: 0, valueFormatter: (v) => fmtAud(v) }]}
          series={[{
            data: pts.map((r) => r.balance_aud), label: 'Closing balance',
            color: AMBER, showMark: true, curve: 'linear',
            valueFormatter: fmtAud,
          }]}
          slotProps={{ legend: { hidden: false, position: { vertical: 'top', horizontal: 'middle' } } }}
          margin={{ left: 70, right: 24, top: 16, bottom: 24 }}
        />
      </CardContent>
    </Card>
  );
}

// Amortization (debt schedule) table — the per-period principal / interest /
// debt-service / closing-balance rows that the bank model carries. Already
// returned by /financials; surfaced here as a collapsible reference table.
function AmortizationTable({ rows }) {
  if (!rows?.length) return null;
  const cell = { fontSize: '0.72rem', py: 0.5, px: 1, borderColor: 'divider' };
  const head = { ...cell, fontWeight: 700, color: 'text.secondary',
                 textTransform: 'uppercase', letterSpacing: '0.03em',
                 fontSize: '0.62rem' };
  const num = { ...cell, fontFamily: 'monospace', textAlign: 'right' };
  const fmtMonth = (iso) => {
    if (!iso) return '—';
    const [y, m] = iso.split('-').map(Number);
    if (!y || !m) return iso;
    return new Date(Date.UTC(y, m - 1, 1))
      .toLocaleDateString('en-AU', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  };
  return (
    <Box sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid',
               borderColor: 'divider', borderRadius: 1, mt: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={head}>Period</TableCell>
            <TableCell sx={{ ...head, textAlign: 'right' }}>Principal</TableCell>
            <TableCell sx={{ ...head, textAlign: 'right' }}>Interest</TableCell>
            <TableCell sx={{ ...head, textAlign: 'right' }}>Debt service (P&amp;I)</TableCell>
            <TableCell sx={{ ...head, textAlign: 'right' }}>Cash sweep</TableCell>
            <TableCell sx={{ ...head, textAlign: 'right' }}>Closing balance</TableCell>
            <TableCell sx={{ ...head, textAlign: 'right' }}>All-in interest</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => {
            const dsvc = (r.principal_aud || 0) + (r.interest_aud || 0);
            return (
              <TableRow key={r.period_start || i} hover>
                <TableCell sx={cell}>{fmtMonth(r.period_start)}</TableCell>
                <TableCell sx={num}>{fmtAud(r.principal_aud)}</TableCell>
                <TableCell sx={num}>{fmtAud(r.interest_aud)}</TableCell>
                <TableCell sx={num}>{fmtAud(dsvc)}</TableCell>
                <TableCell sx={num}>{r.sweep_aud ? fmtAud(r.sweep_aud) : '—'}</TableCell>
                <TableCell sx={num}>{fmtAud(r.balance_aud)}</TableCell>
                <TableCell sx={num}>
                  {r.coupon_pct == null ? '—' : `${Number(r.coupon_pct).toFixed(2)}%`}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// Receivables Aging tile removed 2026-06-11 — it predated this screen's
// Debt Metrics scope (6de1720), only surfaced once allocation data landed,
// and was never an ask. Restore from git history if a receivables view
// gets prioritised; the /receivables endpoint is untouched.

// ── Page ───────────────────────────────────────────────────────────────────

// Period options: current quarter + past 8 quarters + past 3 full years.
// Built once on mount — list doesn't change while the page is open.
function buildPeriodOptions() {
  const today = new Date();
  const cy    = today.getFullYear();
  const cqm   = Math.floor(today.getMonth() / 3) * 3;  // 0/3/6/9
  const out = [];
  for (let i = 0; i < 9; i++) {
    const m = cqm - i * 3;
    let year = cy, mm = m;
    while (mm < 0) { year -= 1; mm += 12; }
    const qn = Math.floor(mm / 3) + 1;
    const start = `${year}-${String(mm + 1).padStart(2, '0')}-01`;
    out.push({
      kind:  'quarter',
      start,
      key:   `quarter:${start}`,
      label: i === 0 ? `Current quarter (Q${qn} ${year})` : `Q${qn} ${year}`,
    });
  }
  for (let i = 1; i <= 3; i++) {
    const y = cy - i;
    out.push({
      kind:  'year',
      start: `${y}-01-01`,
      key:   `year:${y}-01-01`,
      label: `${y} (full year)`,
    });
  }
  return out;
}

const BUCKET_OPTIONS = {
  quarter: ['daily', 'weekly', 'monthly', 'quarterly'],
  year:    ['weekly', 'monthly', 'quarterly', 'yearly'],
};

// ── Covenant tracker + forecast ─────────────────────────────────────────────
// Current status per covenant PLUS a forecast: the rolling-12m metric across
// the model horizon vs the threshold, so the user sees WHEN a covenant is
// projected to breach (not just today's headroom).
const COV_STATUS = {
  pass:    { color: '#1f9d57', label: 'Pass' },
  warn:    { color: '#d98a2b', label: 'Tight' },
  breach:  { color: '#e5384f', label: 'Breach' },
  unknown: { color: '#64748b', label: 'No data' },
};

function CovenantTracker({ covenants }) {
  const rows = (covenants || []).filter(Boolean);
  if (!rows.length) return null;
  const fmtDate = (iso) => {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00`);
    return isNaN(d) ? iso : d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  };
  const fmtVal = (cov, v) => {
    if (v == null) return '—';
    const isRatio = (cov.unit || '').toLowerCase().includes('x') || (cov.code || '').includes('DSCR');
    return isRatio ? fmtX(v) : fmtAud(v);
  };

  return (
    <Card variant="outlined" sx={{ mb: 3, borderColor: 'divider' }}>
      <CardContent>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 1.5 }}>
          Covenant tracker &amp; forecast
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Covenant</TableCell>
                <TableCell align="right">Current</TableCell>
                <TableCell align="right">Threshold</TableCell>
                <TableCell align="right">Headroom</TableCell>
                <TableCell align="right">Min (horizon)</TableCell>
                <TableCell>First projected breach</TableCell>
                <TableCell align="center">Trajectory</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((cov) => {
                const st = COV_STATUS[cov.status] || COV_STATUS.unknown;
                const fc = cov.forecast;
                const pts = fc?.points || [];
                const breachIso = fc?.first_breach_period;
                return (
                  <TableRow key={cov.id || cov.code} hover>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Chip label={st.label} size="small"
                              sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                                    bgcolor: `${st.color}22`, color: st.color }} />
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{cov.label}</Typography>
                          {cov.clause_ref && (
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>{cov.clause_ref}</Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                      {fmtVal(cov, cov.current)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {cov.threshold_op} {fmtVal(cov, cov.threshold)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums',
                                                   color: cov.headroom_pct == null ? 'text.disabled'
                                                          : cov.headroom_pct >= 0 ? '#1f9d57' : '#e5384f' }}>
                      {fmtPct(cov.headroom_pct)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums',
                                                   color: fc?.min_headroom_pct == null ? 'text.disabled'
                                                          : fc.min_headroom_pct >= 0 ? 'text.primary' : '#e5384f' }}>
                      {fc ? fmtPct(fc.min_headroom_pct) : '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {!fc ? <Typography variant="caption" color="text.disabled">n/a</Typography>
                        : breachIso
                          ? <Chip label={fmtDate(breachIso)} size="small"
                                  sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                                        bgcolor: '#e5384f22', color: '#e5384f' }} />
                          : <Typography variant="caption" sx={{ color: '#1f9d57', fontWeight: 600 }}>
                              None in horizon
                            </Typography>}
                    </TableCell>
                    <TableCell align="center" sx={{ width: 180 }}>
                      {pts.length > 1 ? (
                        <LineChart
                          height={44} width={170}
                          margin={{ top: 4, bottom: 4, left: 4, right: 4 }}
                          xAxis={[{ data: pts.map((_, i) => i), scaleType: 'point' }]}
                          leftAxis={null} bottomAxis={null}
                          series={[
                            { data: pts.map((p) => p.value), color: st.color, showMark: false,
                              curve: 'linear' },
                            { data: pts.map(() => cov.threshold), color: '#94a3b8', showMark: false,
                              curve: 'linear' },
                          ]}
                          slotProps={{ legend: { hidden: true } }}
                        />
                      ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1 }}>
          Forecast = trailing-12-month metric rolled across the budget horizon vs the covenant threshold.
          Line = projected covenant value; grey = threshold.
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function FacilityCovenants() {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState('generation');
  const [bucket, setBucket] = useState('weekly');
  // Period picker — defaults to the current quarter so the page reads
  // the same as before the picker existed; users can switch to a past
  // quarter or a full year to compare against history.
  const periodOptions = useMemo(() => buildPeriodOptions(), []);
  const [period, setPeriod] = useState(periodOptions[0]);
  // Side panels — non-blocking fetches. Tile renders skeleton or hides when null.
  const [dsra,        setDsra]        = useState(null);
  const [debtService, setDebtService] = useState(null);
  const [debtSchedule, setDebtSchedule] = useState(null);
  const [covenants,   setCovenants]   = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [finLoading,  setFinLoading]  = useState(true);

  // End of the selected period (inclusive) — quarter = start + 3mo − 1 day,
  // year = 31 Dec. Passed to /financials so the debt-service / DSRA tiles
  // reflect the same window as the period picker, not a fixed YTD default.
  const periodEnd = useMemo(() => {
    // Parse the YYYY-MM-DD components directly (not via `new Date(str)`,
    // which is UTC-parsed and can land on the previous day/month in
    // UTC-negative timezones, shifting the quarter).
    const [y, m] = period.start.split('-').map(Number);   // m is 1-based
    if (period.kind === 'year') return `${y}-12-31`;
    // Last day of the quarter = day 0 of the month after the 3-month span.
    const last = new Date(Date.UTC(y, (m - 1) + 3, 0));
    const yy = last.getUTCFullYear();
    const mm = String(last.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(last.getUTCDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  }, [period]);

  // Reset bucket to a sensible default whenever the period kind changes
  // and the current bucket isn't valid for that kind (e.g. 'daily' on
  // a year period, or 'yearly' on a quarter period).
  useEffect(() => {
    if (!BUCKET_OPTIONS[period.kind].includes(bucket)) {
      setBucket(period.kind === 'quarter' ? 'weekly' : 'monthly');
    }
  }, [period.kind, bucket]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(
        `${API}/api/portfolios/${portfolioId}/financial-models/period-progress`,
        { params: {
            period_kind:  period.kind,
            period_start: period.start,
            bucket,
          } });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, bucket, period]);
  useEffect(() => { load(); }, [load]);

  // Side fetch: DSRA + debt-service summary + debt schedule from
  // /financials (scoped to the selected period) — independent of the main
  // period-progress fetch so it never blocks the headline cards.
  useEffect(() => {
    let alive = true;
    setFinLoading(true);
    axios.get(`${API}/api/portfolios/${portfolioId}/financials`,
              { params: { start: period.start, end: periodEnd } })
      .then((fin) => {
        if (!alive) return;
        setDsra(fin.data?.dsra ?? null);
        setDebtService(fin.data?.debt_service ?? null);
        setDebtSchedule(fin.data?.debt_schedule ?? null);
        setCovenants(fin.data?.covenants ?? null);
      })
      .catch(() => {})
      .finally(() => { if (alive) setFinLoading(false); });
    return () => { alive = false; };
  }, [portfolioId, period.start, periodEnd]);

  const handleDownloadLenderPack = useCallback(async () => {
    try {
      const resp = await axios.get(
        `${API}/api/portfolios/${portfolioId}/lender-pack.pdf`,
        { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      // Server already sets filename via Content-Disposition; mirror it client-side
      // so popup-blocked downloads still get a useful name.
      a.download = `quarterly-summary-${portfolioId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Summary download failed: ${e.response?.data?.error || e.message}`);
    }
  }, [portfolioId]);

  // Only show the full-page spinner on the first load. For subsequent
  // bucket changes we keep the page rendered (with stale data) so it
  // doesn't flash to a centred spinner and feel like a full reload.
  if (loading && !data) {
    return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>;
  }
  if (err && !data) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 2 }}>
          Debt Metrics
        </Typography>
        <Alert severity="error">{err}</Alert>
      </Box>
    );
  }
  if (!data || data.available === false) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 0.5 }}>
          Debt Metrics
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Current-quarter progress vs the active financial model.
        </Typography>
        <Card sx={{ p: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 1 }}>
            Not available
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 460, mx: 'auto', mb: 3 }}>
            {data?.reason || 'No active financial model for this portfolio.'}{' '}
            Upload and activate a financial model in Documents to track the
            current quarter against forecast.
          </Typography>
          <Button variant="contained"
                  onClick={() => navigate(`/portfolios/${portfolioId}/contracts`)}>
            Documents
          </Button>
        </Card>
      </Box>
    );
  }

  // Backend returns `period` (kind-aware) plus `quarter` for backward
  // compat when kind=quarter. Read whichever exists so this works
  // against both old and new backends during deploy.
  const q = data.period || data.quarter;
  const metrics = data.metrics || {};
  const series = data.series;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
              spacing={1.5} sx={{ mb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em' }}>
            Debt Metrics
          </Typography>
          <Select
            size="small"
            value={period.key}
            onChange={(e) => {
              const opt = periodOptions.find((o) => o.key === e.target.value);
              if (opt) setPeriod(opt);
            }}
            sx={{
              height: 28, fontWeight: 700, fontSize: '0.78rem',
              '& .MuiSelect-select': { py: 0.25, pl: 1.25 },
            }}
          >
            {periodOptions.map((o) => (
              <MenuItem key={o.key} value={o.key} sx={{ fontSize: '0.82rem' }}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            day {q.days_elapsed} of {q.days_total} · {q.days_remaining} remaining
          </Typography>
        </Stack>
        <Button
          variant="outlined" size="small"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleDownloadLenderPack}
          sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 600 }}
        >
          Summary download
        </Button>
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
        Click a card to load its in-period progression below.
      </Typography>

      {/* 5 cards — the 4 progress metrics + the debt service schedule.
          The debt service card hides itself until /financials returns. */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {METRICS.map((m) => (
          <MetricCard
            key={m.key}
            m={m}
            data={metrics[m.key]}
            quarter={q}
            series={series}
            thresholds={data.thresholds}
            selected={selected === m.key}
            onClick={() => setSelected(m.key)}
          />
        ))}
        {debtService ? (
          <DebtServiceTile
            ds={debtService} periodLabel={period.label}
            selected={selected === 'debt_service'}
            onClick={() => setSelected('debt_service')}
          />
        ) : (finLoading && <DebtServiceSkeleton />)}
      </Stack>

      {/* Supplementary finance tile — DSRA. Hides itself when the bank
          model doesn't provide it. */}
      {dsra && (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
          <DsraTile dsra={dsra} />
        </Stack>
      )}

      {/* Covenant tracker + horizon forecast — shown only on the DSCR view
          (these are DSCR covenants), not repeated under every metric chart. */}
      {selected === 'dscr' && <CovenantTracker covenants={covenants} />}

      {/* Big chart for selected metric — Debt Service tile loads the P&I
          schedule chart; metric cards load the in-period progression. */}
      {selected === 'debt_service' ? (
        <>
          <DebtServiceChart rows={debtSchedule}
                            periodStart={period.start} periodEnd={periodEnd} />
          <BalanceChart rows={debtSchedule}
                        periodStart={period.start} periodEnd={periodEnd} />
        </>
      ) : (
        <BigChart metric={selected} series={series} thresholds={data.thresholds}
                   bucket={bucket} onBucketChange={setBucket}
                   periodKind={period.kind}
                   bucketOptions={BUCKET_OPTIONS[period.kind]} />
      )}

      {/* Debt schedule (amortization) — collapsible reference table.
          Only on the Debt Service view, not under every metric chart. */}
      {selected === 'debt_service' && debtSchedule?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            onClick={() => setShowSchedule((s) => !s)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {showSchedule ? 'Hide' : 'Show'} debt schedule (amortization) ·{' '}
            {debtSchedule.length} periods
          </Button>
          <Collapse in={showSchedule}>
            <AmortizationTable rows={debtSchedule} />
          </Collapse>
        </Box>
      )}
    </Box>
  );
}
